import { Box, Grid, Paper, Typography, Skeleton, Chip } from '@mui/material';
import {
  Building2,
  Ticket,
  AlertTriangle,
  Activity,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
import { StatCard } from '../../components/StatCard';
import { ADMIN_ROUTES } from '../../router/routes';

const STAT_CARDS = [
  {
    key: 'totalInstitutes' as const,
    label: 'Total Institutes',
    color: '#8A70D6',
    icon: <Building2 size={17} />,
  },
  {
    key: 'activeInstitutes' as const,
    label: 'Active Institutes',
    color: '#10B981',
    icon: <CheckCircle2 size={17} />,
  },
  {
    key: 'openTickets' as const,
    label: 'Open Tickets',
    color: '#F97316',
    icon: <Ticket size={17} />,
  },
  {
    key: 'slaBreaches' as const,
    label: 'SLA Breaches',
    color: '#EF4444',
    icon: <AlertTriangle size={17} />,
  },
  {
    key: 'auditEventsToday' as const,
    label: 'Audit Events Today',
    color: '#3B82F6',
    icon: <Activity size={17} />,
  },
  {
    key: 'pendingOnboarding' as const,
    label: 'Pending Onboarding',
    color: '#F59E0B',
    icon: <Clock size={17} />,
  },
];

const QUICK_LINKS = [
  { label: 'Manage Institutes', route: ADMIN_ROUTES.INSTITUTES, color: '#8A70D6' },
  { label: 'Feature Flags', route: ADMIN_ROUTES.FEATURE_FLAGS, color: '#10B981' },
  { label: 'Support Tickets', route: ADMIN_ROUTES.SUPPORT_TICKETS, color: '#F97316' },
  { label: 'Audit Logs', route: ADMIN_ROUTES.AUDIT_LOGS, color: '#3B82F6' },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' as const } },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useAdminDashboard();

  return (
    <Box>
      {/* Stats grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {STAT_CARDS.map((card) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={card.key}>
              <motion.div variants={itemVariants}>
                <StatCard
                  label={card.label}
                  value={stats?.[card.key] ?? 0}
                  icon={card.icon}
                  color={card.color}
                  loading={isLoading}
                />
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>

      <Grid container spacing={2}>
        {/* Quick links */}
        <Grid size={{ xs: 12, md: 4 }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.22 }}>
            <Paper
              sx={{
                p: 2.5,
                border: '1px solid rgba(255,255,255,0.06)',
                height: '100%',
              }}
            >
              <Typography variant="overline" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
                Quick Access
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {QUICK_LINKS.map((link) => (
                  <Box
                    key={link.route}
                    onClick={() => navigate(link.route)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderRadius: '7px',
                      border: '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      transition: 'all 150ms ease',
                      '&:hover': {
                        backgroundColor: `${link.color}12`,
                        borderColor: `${link.color}40`,
                        transform: 'translateX(3px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: link.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {link.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </motion.div>
        </Grid>

        {/* Health overview */}
        <Grid size={{ xs: 12, md: 8 }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.22 }}>
            <Paper
              sx={{
                p: 2.5,
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Typography variant="overline" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
                Product Health
              </Typography>
              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={40} sx={{ borderRadius: '7px' }} />
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    {
                      label: 'Institute Activation Rate',
                      value: stats
                        ? Math.round((stats.activeInstitutes / Math.max(stats.totalInstitutes, 1)) * 100)
                        : 0,
                      color: '#10B981',
                      suffix: '%',
                    },
                    {
                      label: 'SLA Compliance',
                      value: stats
                        ? Math.max(
                            0,
                            100 - Math.round((stats.slaBreaches / Math.max(stats.openTickets, 1)) * 100)
                          )
                        : 100,
                      color: '#3B82F6',
                      suffix: '%',
                    },
                    {
                      label: 'Onboarding Pending',
                      value: stats?.pendingOnboarding ?? 0,
                      color: '#F59E0B',
                      suffix: ' institutes',
                    },
                  ].map((metric) => (
                    <Box
                      key={metric.label}
                      sx={{
                        px: 2,
                        py: 1.25,
                        borderRadius: '7px',
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {metric.label}
                      </Typography>
                      <Chip
                        label={`${metric.value}${metric.suffix}`}
                        size="small"
                        sx={{
                          backgroundColor: `${metric.color}18`,
                          color: metric.color,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: 24,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
}
